import { v1 as uuid } from 'uuid';

const CLONE_KEY_PREFIX = 'pendingCloneAlpha_';
const CLONE_TTL_MS = 30000; // 30 seconds

export interface CloneAlphaPayload {
  code: string;
  settings: {
    region?: string;
    universe?: string;
    delay?: string;
    decay?: string;
    neutralization?: string;
    truncation?: string;
    language?: string;
    pasteurization?: string;
    unitHandling?: string;
    nanHandling?: string;
    componentActivation?: string;
    testPeriod?: string;
    maxTrade?: string;
    maxPosition?: string;
  };
  visualization?: boolean;
  superAlpha?: boolean;
  combo?: string;
  selection?: string;
  type?: string;
  color?: string | null;
  name?: string | null;
  tags?: string[];
  category?: string | null;
}

/**
 * Check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__ls_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Store clone payload in localStorage and return the nonce
 */
export function storeClonePayload(payload: CloneAlphaPayload): string {
  const nonce = uuid();
  const entry = {
    payload,
    timestamp: Date.now(),
  };
  localStorage.setItem(`${CLONE_KEY_PREFIX}${nonce}`, JSON.stringify(entry));
  return nonce;
}

/**
 * Retrieve and remove clone payload from localStorage by nonce.
 * Returns null if not found or expired.
 */
export function consumeClonePayload(nonce: string): CloneAlphaPayload | null {
  const key = `${CLONE_KEY_PREFIX}${nonce}`;
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  localStorage.removeItem(key);

  try {
    const entry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CLONE_TTL_MS) {
      return null; // expired
    }
    return entry.payload as CloneAlphaPayload;
  } catch {
    return null;
  }
}

/**
 * Clean up any expired clone payloads from localStorage
 */
export function cleanupExpiredClonePayloads(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CLONE_KEY_PREFIX)) {
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const entry = JSON.parse(raw);
          if (Date.now() - entry.timestamp > CLONE_TTL_MS) {
            keysToRemove.push(key);
          }
        }
      } catch {
        keysToRemove.push(key);
      }
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}
